/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.
*/

export const COREBANK_API_BASE_URL_OVERRIDE_KEY = "corebank-api-base-url";
export const ENABLE_DEMO_HEADER_OVERRIDE_KEY =
  "bank-enable-demo-header-override";
export const PUBLIC_ACCOUNTS_OVERRIDE_KEY = "bank-public-accounts";

export interface DeveloperOverrides {
  corebankApiBaseUrl?: string;
  overrideEnableDemoHeader?: boolean;
  overrideShowPublicAccounts?: boolean;
}

type ReadableStorage = Pick<Storage, "getItem">;
type WritableStorage = Pick<Storage, "setItem" | "removeItem">;

export function readDeveloperOverrides(
  storage: ReadableStorage | undefined,
): DeveloperOverrides {
  if (!storage) return {};
  const corebankApiBaseUrl =
    storage.getItem(COREBANK_API_BASE_URL_OVERRIDE_KEY)?.trim() || undefined;
  const enableDemoHeaderValue = storage.getItem(
    ENABLE_DEMO_HEADER_OVERRIDE_KEY,
  );
  const overrideEnableDemoHeader =
    enableDemoHeaderValue === "true"
      ? true
      : enableDemoHeaderValue === "false"
        ? false
        : undefined;
  const showPublicAccountsValue = storage.getItem(PUBLIC_ACCOUNTS_OVERRIDE_KEY);
  const overrideShowPublicAccounts =
    showPublicAccountsValue === "true"
      ? true
      : showPublicAccountsValue === "false"
        ? false
        : undefined;
  return {
    ...(corebankApiBaseUrl ? { corebankApiBaseUrl } : {}),
    ...(overrideEnableDemoHeader === undefined
      ? {}
      : { overrideEnableDemoHeader }),
    ...(overrideShowPublicAccounts === undefined
      ? {}
      : { overrideShowPublicAccounts }),
  };
}

export function writeDeveloperOverrides(
  storage: WritableStorage,
  overrides: DeveloperOverrides,
): void {
  if (overrides.corebankApiBaseUrl) {
    storage.setItem(
      COREBANK_API_BASE_URL_OVERRIDE_KEY,
      overrides.corebankApiBaseUrl,
    );
  } else {
    storage.removeItem(COREBANK_API_BASE_URL_OVERRIDE_KEY);
  }
  if (overrides.overrideEnableDemoHeader === undefined) {
    storage.removeItem(ENABLE_DEMO_HEADER_OVERRIDE_KEY);
  } else {
    storage.setItem(
      ENABLE_DEMO_HEADER_OVERRIDE_KEY,
      String(overrides.overrideEnableDemoHeader),
    );
  }
  if (overrides.overrideShowPublicAccounts === undefined) {
    storage.removeItem(PUBLIC_ACCOUNTS_OVERRIDE_KEY);
  } else {
    storage.setItem(
      PUBLIC_ACCOUNTS_OVERRIDE_KEY,
      String(overrides.overrideShowPublicAccounts),
    );
  }
}

export function resolveBooleanOverride(
  configured: boolean,
  override: boolean | undefined,
): boolean {
  return override ?? configured;
}
